import { createCRUDService } from './genericCRUD';
import { navigationModuleEntity } from '@/config/entities/navigationModule';
import { navigationScreenEntity } from '@/config/entities/navigationScreen';
import { navigationNodeEntity } from '@/config/entities/navigationNode';
import api from './api';

// Create CRUD services for navigation entities
export const navigationModuleService = createCRUDService(navigationModuleEntity);
export const navigationScreenService = createCRUDService(navigationScreenEntity);
export const navigationNodeService = createCRUDService(navigationNodeEntity);

// Extended navigation management service with additional methods
export class NavigationCRUDService {
  // Module management
  static async getModules(params: any = {}) {
    try {
      // Use the navigation API endpoint with search and pagination support
      const response = await api.get('/navigation/modules', { params });
      const modules = response.data.data || [];
      
      // Filter out unwanted fields for table display
      const filteredModules = modules.map((module: any) => {
        const { sort_order, is_system_module, ...filteredModule } = module;
        return filteredModule;
      });
      
      // Return in unified pagination format
      return { 
        data: filteredModules,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching modules:', error);
      return { 
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false
        }
      };
    }
  }

  /**
   * Get all modules (active and inactive) for use in dropdowns
   * This is specifically for parent module selection where we want to show all available modules
   */
  static async getAllModules(): Promise<{ data: any[] }> {
    try {
      const response = await api.get('/navigation/modules/all');
      return { data: response.data.data || [] };
    } catch (error) {
      console.error('Error fetching all modules:', error);
      return { data: [] };
    }
  }

  static async getModuleById(id: string) {
    // Get all navigation data and find the specific module
    const response = await api.get('/rbac/navigation/items');
    const navigationData = response.data.data;
    const modules = navigationData.modules || [];
    const module = modules.find((module: any) => module.id === id);
    
    // Filter out unwanted fields for consistency
    if (module) {
      const { sort_order, is_system_module, ...filteredModule } = module;
      return { data: filteredModule };
    }
    
    return { data: module };
  }

  static async createModule(data: any) {
    // Add itemType for the RBAC endpoint
    const createData = {
      ...data,
      itemType: 'module'
    };
    return api.post('/rbac/navigation/items', createData);
  }

  static async updateModule(id: string, data: any) {
    return api.put(`/rbac/navigation/items/module/${id}`, data);
  }

  static async deleteModule(id: string) {
    return api.delete(`/rbac/navigation/items/module/${id}`);
  }

  static async copyModule(idOrItem: string | any) {
    // Handle both string ID and item object
    const id = typeof idOrItem === 'string' ? idOrItem : idOrItem?.id;
    if (!id) {
      throw new Error('Invalid module ID provided for copy operation');
    }
    return navigationModuleService.copy(id);
  }

  // Screen management
  static async getScreens(params: any = {}) {
    try {
      const response = await api.get('/navigation/screens', { params });
      const screens = response.data.data || [];
      
      // Include all fields including screen_code for table display
      // No filtering needed - show all fields
      
      return { 
        data: screens,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching screens:', error);
      return { data: [] };
    }
  }

  static async getScreenById(id: string) {
    // Get all navigation data and find the specific screen
    const response = await api.get('/rbac/navigation/items');
    const navigationData = response.data.data;
    const screens = navigationData.screens || [];
    const screen = screens.find((screen: any) => screen.id === id);
    
    // Filter out unwanted fields for consistency
    if (screen) {
      const { screen_code, icon, sort_order, is_system_screen, ...filteredScreen } = screen;
      return { data: filteredScreen };
    }
    
    return { data: screen };
  }

  static async createScreen(data: any) {
    // Add itemType for the RBAC endpoint
    const createData = {
      ...data,
      itemType: 'screen'
    };
    return api.post('/rbac/navigation/items', createData);
  }

  static async updateScreen(id: string, data: any) {
    return api.put(`/navigation/screens/${id}`, data);
  }

  static async deleteScreen(id: string) {
    return api.delete(`/rbac/navigation/items/screen/${id}`);
  }

  static async copyScreen(idOrItem: string | any) {
    // Handle both string ID and item object
    const id = typeof idOrItem === 'string' ? idOrItem : idOrItem?.id;
    if (!id) {
      throw new Error('Invalid screen ID provided for copy operation');
    }
    return navigationScreenService.copy(id);
  }

  // Note: Node management is not supported by the backend RBAC system
  // Nodes are managed through the navigation_nodes table directly
  // These methods are kept for compatibility but will not work with the RBAC endpoints

  // Node management
  static async getNodes(params: any = {}) {
    try {
      const response = await api.get('/navigation/nodes', { params });
      
      // Handle unified pagination response format
      if (response.data && response.data.data && response.data.pagination) {
        return {
          data: response.data.data,
          pagination: response.data.pagination
        };
      }
      
      // Fallback for legacy format
      return { 
        data: response.data.data || response.data,
        pagination: {
          page: 1,
          limit: 10,
          total: (response.data.data || response.data).length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false
        }
      };
    } catch (error) {
      console.error('Error fetching nodes:', error);
      return { 
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false
        }
      };
    }
  }

  static async getNodeById(id: string) {
    try {
      const response = await api.get(`/navigation/nodes/${id}`);
      return { data: response.data.data || response.data };
    } catch (error) {
      console.error('Error fetching node:', error);
      throw error;
    }
  }

  static async createNode(data: any) {
    try {
      const response = await api.post('/navigation/nodes', data);
      return { data: response.data.data || response.data };
    } catch (error) {
      console.error('Error creating node:', error);
      throw error;
    }
  }

  static async updateNode(id: string, data: any) {
    try {
      const response = await api.put(`/navigation/nodes/${id}`, data);
      return { data: response.data.data || response.data };
    } catch (error) {
      console.error('Error updating node:', error);
      throw error;
    }
  }

  static async deleteNode(id: string) {
    try {
      const response = await api.delete(`/navigation/nodes/${id}`);
      return { data: response.data.data || response.data };
    } catch (error) {
      console.error('Error deleting node:', error);
      throw error;
    }
  }

  static async copyNode(idOrItem: string | any) {
    // Handle both string ID and item object
    const id = typeof idOrItem === 'string' ? idOrItem : idOrItem?.id;
    if (!id) {
      throw new Error('Invalid node ID provided for copy operation');
    }
    try {
      const node = await this.getNodeById(id);
      const nodeData = node.data;
      const copyData = {
        ...nodeData,
        node_code: `${nodeData.node_code}_COPY`,
        display_name: `${nodeData.display_name} (Copy)`
      };
      delete copyData.id;
      delete copyData.created_at;
      delete copyData.updated_at;
      
      return await this.createNode(copyData);
    } catch (error) {
      console.error('Error copying node:', error);
      throw error;
    }
  }

  // Bulk operations
  static async bulkUpdateSortOrders(items: Array<{ id: string; sort_order: number; type: string }>) {
    const updates = items.map(item => {
      if (item.type === 'module') {
        return this.updateModule(item.id, { sort_order: item.sort_order });
      } else if (item.type === 'screen') {
        return this.updateScreen(item.id, { sort_order: item.sort_order });
      }
      // Note: node type is not supported in bulk operations
    });

    return Promise.all(updates.filter(Boolean));
  }

  // Get options for select fields
  static async getModuleOptions() {
    const { data } = await this.getModules();
    return data.map((module: any) => ({
      value: module.id,
      label: module.display_name
    }));
  }

  static async getScreenOptions() {
    const { data } = await this.getScreens();
    return data.map((screen: any) => ({
      value: screen.id,
      label: screen.display_name
    }));
  }
}
